import { useEffect, useRef } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';
import { useRouter } from 'next/router';

import config from '@/config';

interface FacebookPixel {
  instance: {
    pixelsByID: Record<string, unknown>;
  };
}

declare global {
  interface Window {
    fbq: FacebookPixel & {
      (...args: unknown[]): unknown;
    };
  }
}

interface UseMetaPixelOptions {
  trackPageViews?: boolean;
}

export const pixelScriptLoadedVar = makeVar(false);

const PAGEVIEW = 'PageView';

function debug(message: string) {
  if (!config('/debug')) return;
  console.debug(`[Meta Pixel] ${message}`);
}

const useMetaPixel = (pixelId?: string, { trackPageViews }: UseMetaPixelOptions = {}) => {
  const router = useRouter();
  const scriptLoaded = useReactiveVar(pixelScriptLoadedVar);

  // Creating trackEvent using a ref allows us to maintain a consistent
  // reference to the function while also giving it access to the latest
  // pixelId & router data.
  const trackEvent = useRef<(...args: unknown[]) => unknown>(() => {});
  trackEvent.current = (eventName: string, eventParams?: unknown) => {
    if (!scriptLoaded) {
      debug(`Triggered ${eventName}, before script finished loading`);
      return;
    }

    if (!pixelId) {
      debug(`Skipping ${eventName}, no pixel id provided`);
      return;
    }

    if (!window.fbq.instance.pixelsByID[pixelId]) {
      window.fbq('set', 'autoConfig', false, pixelId);
      window.fbq('init', pixelId);
      debug(`Initialized pixel ${pixelId}`);
    }

    // Over the course of the user's session they may navigate
    // to pages utilizing different pixels. To avoid accidentally
    // triggering an event to the wrong pixel, we use the 'trackSingle'
    // param ensuring it only applies to the specified pixel.
    window.fbq('trackSingle', pixelId, eventName, eventParams);
    let log = `Triggered ${eventName} to pixel ${pixelId}`;
    if (eventName === PAGEVIEW) log += ` (${router.asPath})`;
    debug(log);
  };

  // Handle pageview events
  const shouldTriggerPageView = scriptLoaded && !!pixelId && trackPageViews;
  useEffect(() => {
    if (!shouldTriggerPageView) return;
    trackEvent.current(PAGEVIEW);
  }, [shouldTriggerPageView, router.asPath]);

  // Return trackEvent function for custom triggers
  return { trackEvent: trackEvent.current };
};

export default useMetaPixel;
